// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { X, Trash2, Plus, Minus, ShoppingCart, Tag, Truck, Store } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function CartDrawer() {
  const { user } = useAuth();
  const {
    isOpen, setIsOpen, items, coupon,
    removeItem, updateQuantity, applyCoupon, removeCoupon,
    subtotal, discount, deliveryFee, tax, total, itemCount,
    fulfillment, setFulfillment,
  } = useCart();
  const navigate = useNavigate();
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [applying, setApplying]       = useState(false);

  if (!isOpen) return null;

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplying(true);
    setCouponError('');
    try {
      await applyCoupon(couponInput.trim());
      setCouponInput('');
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleCheckout = () => {
    setIsOpen(false);
    navigate(user ? '/checkout' : '/login');
  };

  return (
    <>
      <div className="cart-overlay" onClick={() => setIsOpen(false)} />
      <div className="cart-drawer">
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px', borderBottom: '1px solid #BBF7D0', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShoppingCart size={20} color="#16A34A" />
            <span style={{ fontSize: 17, fontWeight: 800, color: '#14532D' }}>Your Cart</span>
            {itemCount > 0 && (
              <span style={{
                background: '#DCFCE7', color: '#16A34A',
                padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700,
              }}>
                {itemCount} item{itemCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={() => setIsOpen(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', display: 'flex' }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Fulfillment toggle */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #F0FDF4' }}>
          <div style={{ display: 'flex', background: '#F0FDF4', borderRadius: 10, padding: 3 }}>
            {[
              { val: 'delivery', icon: Truck,  label: 'Delivery' },
              { val: 'pickup',   icon: Store,  label: 'Pickup'   },
            ].map(opt => (
              <button
                key={opt.val}
                onClick={() => setFulfillment(opt.val)}
                style={{
                  flex: 1, padding: '8px 12px',
                  background: fulfillment === opt.val ? '#16A34A' : 'transparent',
                  color: fulfillment === opt.val ? '#fff' : '#64748B',
                  border: 'none', borderRadius: 8, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                }}
              >
                <opt.icon size={15} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94A3B8' }}>
              <ShoppingCart size={48} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.3 }} />
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Your cart is empty</div>
              <button className="btn btn-primary" onClick={() => { setIsOpen(false); navigate('/products'); }}>
                Start Shopping
              </button>
            </div>
          ) : (
            items.map(item => {
              const prod = item.grocery_products;
              const lineTot = item.unit_price_cents * item.quantity;
              return (
                <div key={item.id} style={{
                  display: 'flex', gap: 12, padding: '12px 0',
                  borderBottom: '1px solid #F0FDF4', alignItems: 'flex-start',
                }}>
                  {/* Emoji placeholder */}
                  <div style={{
                    width: 56, height: 56, borderRadius: 10, flexShrink: 0,
                    background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 28,
                  }}>
                    {getCategoryEmoji(prod?.unit)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#14532D', lineHeight: 1.3, marginBottom: 2 }}>
                      {prod?.name || 'Product'}
                    </div>
                    <div style={{ fontSize: 12, color: '#94A3B8' }}>
                      {fmt(item.unit_price_cents)}/{prod?.unit || 'each'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          style={{
                            width: 28, height: 28, borderRadius: 8,
                            background: '#F0FDF4', border: '1px solid #BBF7D0',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Minus size={14} color="#16A34A" />
                        </button>
                        <span style={{ fontSize: 14, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          style={{
                            width: 28, height: 28, borderRadius: 8,
                            background: '#16A34A', border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Plus size={14} color="#fff" />
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#14532D' }}>{fmt(lineTot)}</span>
                        <button
                          onClick={() => removeItem(item.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', display: 'flex' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Coupon + Summary */}
        {items.length > 0 && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid #BBF7D0', flexShrink: 0 }}>
            {/* Coupon input */}
            {coupon ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#F0FDF4', borderRadius: 10, padding: '8px 12px', marginBottom: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag size={16} color="#16A34A" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>{coupon.code}</span>
                  <span style={{ fontSize: 12, color: '#64748B' }}>{coupon.title}</span>
                </div>
                <button onClick={removeCoupon} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', display: 'flex' }}>
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input
                  className="input"
                  style={{ flex: 1, minHeight: 36, padding: '7px 12px', fontSize: 13 }}
                  placeholder="Coupon code..."
                  value={couponInput}
                  onChange={e => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                />
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleApplyCoupon}
                  disabled={applying || !couponInput}
                >
                  Apply
                </button>
              </div>
            )}
            {couponError && (
              <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 10 }}>{couponError}</div>
            )}

            {/* Summary */}
            {[
              { label: 'Subtotal',  value: fmt(subtotal)  },
              discount > 0 && { label: 'Discount',  value: `-${fmt(discount)}`, color: '#16A34A' },
              { label: fulfillment === 'pickup' ? 'Pickup' : 'Delivery', value: deliveryFee === 0 ? 'FREE' : fmt(deliveryFee), color: deliveryFee === 0 ? '#16A34A' : undefined },
              { label: 'Tax (7%)',  value: fmt(tax) },
            ].filter(Boolean).map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748B', marginBottom: 4 }}>
                <span>{row.label}</span>
                <span style={{ color: row.color || '#14532D', fontWeight: row.color ? 700 : 400 }}>{row.value}</span>
              </div>
            ))}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 17, fontWeight: 800, color: '#14532D',
              paddingTop: 10, borderTop: '2px solid #BBF7D0', marginTop: 6, marginBottom: 16,
            }}>
              <span>Total</span>
              <span>{fmt(total)}</span>
            </div>

            <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={handleCheckout}>
              {user ? `Proceed to Checkout — ${fmt(total)}` : 'Sign In to Checkout'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function getCategoryEmoji(unit) {
  const map = {
    lb: '⚖️', oz: '⚖️', kg: '⚖️',
    bunch: '🥦', bag: '🛍️', gallon: '🥛',
    dozen: '🥚', loaf: '🍞', pack: '📦',
    bottle: '🍶', jug: '🍶', jar: '🫙',
    box: '📦', tub: '🫙', block: '🧀', each: '🛒',
  };
  return map[unit] || '🛒';
}
